/**
 * Overrides console.log, console.info, console.debug, console.error, and
 * console.warn to capture frontend log output and relay it to the backend
 * for Application Insights ingestion.
 *
 * Also installs global window.onerror and unhandledrejection handlers to
 * catch fatal errors that occur outside the React component tree.
 *
 * The original console methods still execute, so DevTools output is unaffected.
 * Entries are batched and flushed periodically to avoid excessive HTTP traffic.
 */

import { FrontendLogEntry, LogLevel } from 'types/logging';
import { LoggingApi } from 'apis/LoggingApi';

// ── State ────────────────────────────────────────────────────────────────────

let queue: FrontendLogEntry[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let isFlushing = false;

// ── Original references (preserved for pass-through) ─────────────────────────

const originalLog = console.log;
const originalInfo = console.info;
const originalDebug = console.debug;
const originalError = console.error;
const originalWarn = console.warn;

// ── Constants ────────────────────────────────────────────────────────────────

const FLUSH_INTERVAL_MS = 5_000;
const MAX_QUEUE_SIZE = 50;
const MAX_MESSAGE_LENGTH = 4_000;

// ── Sensitive data masking ────────────────────────────────────────────────────

const MASKED = '[REDACTED]';

/** Keys whose values should always be redacted when seen in serialized JSON. */
const SENSITIVE_KEY_PATTERN =
    /("(?:[^"]*(?:password|passwd|secret|token|apikey|api_key|authorization|bearer|credential|ssn|creditcard|credit_card|cardnumber|card_number|cvv|cvc)[^"]*)")\s*:\s*"[^"]*"/gi;

/** Email addresses. */
const EMAIL_PATTERN = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;

/** JWT tokens (three base64url segments separated by dots). */
const JWT_PATTERN = /eyJ[A-Za-z0-9_-]{10,}\.eyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}/g;

/** Connection-string-style Password= or Pwd= values. */
const CONN_STRING_PASSWORD_PATTERN = /((?:Password|Pwd)\s*=\s*)([^;]+)/gi;

const maskSensitiveData = (text: string): string => text
    .replace(SENSITIVE_KEY_PATTERN, (_, key: string) => `${key}:"${MASKED}"`)
    .replace(JWT_PATTERN, MASKED)
    .replace(EMAIL_PATTERN, MASKED)
    .replace(CONN_STRING_PASSWORD_PATTERN, `$1${MASKED}`);

// ── Serialization ────────────────────────────────────────────────────────────

const safeSerialize = (args: unknown[]): string => {

    const parts = args.map(arg => {

        try {

            if (arg instanceof Error) return `${arg.name}: ${arg.message}`;
            if (typeof arg === 'string') return arg;
            return JSON.stringify(arg);

        } catch {

            return String(arg);

        }

    });

    const joined = parts.join(' ');
    const masked = maskSensitiveData(joined);

    return masked.length > MAX_MESSAGE_LENGTH
        ? masked.substring(0, MAX_MESSAGE_LENGTH) + '...[truncated]'
        : masked;

};

const extractStack = (args: unknown[]): string | null => {

    for (const arg of args) {

        if (arg instanceof Error && arg.stack) return arg.stack;

    }

    return null;

};

// ── Queue + Flush ────────────────────────────────────────────────────────────

const enqueue = (level: LogLevel, args: unknown[]): void => {

    const entry: FrontendLogEntry = {
        Level: level,
        Message: safeSerialize(args),
        Stack: extractStack(args),
        Timestamp: new Date().toISOString(),
        Url: window.location.href
    };

    queue.push(entry);

    if (queue.length >= MAX_QUEUE_SIZE) {

        flush();

    } else if (!flushTimer) {

        flushTimer = setTimeout(flush, FLUSH_INTERVAL_MS);

    }

};

const flush = (): void => {

    if (flushTimer) {

        clearTimeout(flushTimer);
        flushTimer = null;

    }

    if (queue.length === 0 || isFlushing) return;

    isFlushing = true;
    const batch = queue;
    queue = [];

    LoggingApi.TrackLogs(batch)
        .catch(() => {

            // Silently drop -- logging the failure would re-enter the override.
        })
        .finally(() => {

            isFlushing = false;

            // Restart timer if entries accumulated during the API call
            if (queue.length > 0 && !flushTimer) {

                flushTimer = setTimeout(flush, FLUSH_INTERVAL_MS);

            }

        });

};

// ── Public API ───────────────────────────────────────────────────────────────

/**
 * Installs the console overrides. Call once, before ReactDOM.render.
 */
export const initConsoleOverride = (): void => {

    console.log = (...args: unknown[]) => {

        originalLog.apply(console, args);
        enqueue('log', args);

    };

    console.info = (...args: unknown[]) => {

        originalInfo.apply(console, args);
        enqueue('info', args);

    };

    console.debug = (...args: unknown[]) => {

        originalDebug.apply(console, args);
        enqueue('debug', args);

    };

    console.error = (...args: unknown[]) => {

        originalError.apply(console, args);
        enqueue('error', args);

    };

    console.warn = (...args: unknown[]) => {

        originalWarn.apply(console, args);
        enqueue('warn', args);

    };

    // Catch errors outside the React tree (global scripts, async callbacks, third-party libs)
    window.onerror = (_message, source, lineno, colno, error) => {

        enqueue('error', [error ?? `Unhandled error at ${source}:${lineno}:${colno}`]);
        return false; // allow default handling as well (e.g. DevTools logging)

    };

    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {

        enqueue('error', [`Unhandled promise rejection: ${event.reason}`]);

    });

    window.addEventListener('beforeunload', () => {

        if (queue.length === 0) return;

        // sendBeacon is specifically designed for page unload scenarios
        // Returns true if browser queued the request, false if it failed
        const sent = navigator.sendBeacon(
            '/Api/Logging/TrackLogs',
            new Blob([JSON.stringify({ entries: queue })], { type: 'application/json' })
        );

        // Only clear queue if beacon was successfully queued
        if (sent) {

            queue = [];

        }

    });

};

/**
 * Immediately sends an ErrorBoundary crash report to the backend,
 * bypassing the batch queue entirely.
 */
export const sendErrorBoundaryReport = (
    error: Error,
    errorInfo: { componentStack: string }
): void => {

    LoggingApi.TrackException(
        error.message,
        error.stack ?? '',
        errorInfo.componentStack,
        window.location.href
    ).catch(() => {

        // Silently drop -- cannot let a logging failure cascade.
    });

};
